import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api, borrarToken, guardarToken, leerToken, ErrorApi } from "../lib/api";

export interface AdminPlataforma {
  nombre: string;
  email: string;
}

interface ValorSesion {
  admin: AdminPlataforma | null;
  cargando: boolean;
  entrar: (email: string, password: string) => Promise<void>;
  salir: () => Promise<void>;
}

const Contexto = createContext<ValorSesion | null>(null);

export function ProveedorSesionPlataforma({ children }: { children: ReactNode }) {
  const [admin, setAdmin] = useState<AdminPlataforma | null>(null);
  const [cargando, setCargando] = useState(true);

  const refrescar = useCallback(async () => {
    if (!leerToken()) {
      setAdmin(null);
      setCargando(false);
      return;
    }
    try {
      setAdmin(await api<AdminPlataforma>("/plataforma/auth/yo"));
    } catch (fallo) {
      if (fallo instanceof ErrorApi && fallo.status === 401) {
        borrarToken();
        setAdmin(null);
      }
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    void refrescar();
  }, [refrescar]);

  const entrar = useCallback(async (email: string, password: string) => {
    const r = await api<{ token: string; admin: AdminPlataforma }>("/plataforma/auth/login", {
      metodo: "POST",
      cuerpo: { email, password },
    });
    guardarToken(r.token);
    setAdmin(r.admin);
  }, []);

  const salir = useCallback(async () => {
    await api("/plataforma/auth/logout", { metodo: "POST" }).catch(() => undefined);
    borrarToken();
    setAdmin(null);
  }, []);

  const valor = useMemo(() => ({ admin, cargando, entrar, salir }), [admin, cargando, entrar, salir]);
  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useSesionPlataforma(): ValorSesion {
  const valor = useContext(Contexto);
  if (!valor) throw new Error("useSesionPlataforma debe usarse dentro de ProveedorSesionPlataforma");
  return valor;
}
